// valet-keychain-qr — qr-batch-generation + qr-label-pdf: super-admin batch
// create/list/reprint + configurable label-sheet PDF export for one
// organization/location. Mounted at
// /super-admin/qr-generator/$orgId/$locationId (super_admin-gated by the
// parent `_authenticated/super-admin` route).

import { ApiError } from '@jq4race/api-client';
import { useTranslation } from '@jq4race/i18n';
import type { CreateQrBatchRequest, QrBatchResponse, QrBatchSummary } from '@jq4race/shared';
import { Button, Card, CardContent, Input, Label } from '@jq4race/ui';
import { useQuery } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import { useCallback, useEffect, useState } from 'react';
import { translateApiError } from '../../../../shared/errors/translateApiError';
import { PageHeader } from '../../../app-shell/ui/PageHeader';
import { listOrganizations } from '../../api/organizations';
import { createQrBatch, getQrBatch, listQrBatches } from '../../api/qr-batches';
import { LabelConfigForm } from '../components/LabelConfigForm';
import { LabelGridPreview } from '../components/LabelGridPreview';
import { QrBatchForm } from '../components/QrBatchForm';
import { QrBatchList } from '../components/QrBatchList';
import type { ExportQrLabelsInput } from '../pdf/export-qr-labels';
import { exportPartCount, type LabelGridConfig, MAX_LABELS_PER_PDF } from '../pdf/label-grid';

const DEFAULT_LABEL_CONFIG: LabelGridConfig = {
  pageSize: 'A4',
  columns: 3,
  rows: 8,
  labelWidthMm: 60,
  labelHeightMm: 30,
  marginMm: 10,
};

interface ExportProgress {
  done: number;
  total: number;
}

export function QrGeneratorPage() {
  const { t } = useTranslation('qr-generator');
  const { orgId, locationId } = useParams({ strict: false });

  const [batches, setBatches] = useState<QrBatchSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [createSuccess, setCreateSuccess] = useState<string | null>(null);

  const [activeBatch, setActiveBatch] = useState<QrBatchResponse | null>(null);
  const [reprintingId, setReprintingId] = useState<string | null>(null);
  const [reprintError, setReprintError] = useState<string | null>(null);

  const [venueName, setVenueName] = useState('');
  const [venueNameTouched, setVenueNameTouched] = useState(false);
  const [labelConfig, setLabelConfig] = useState<LabelGridConfig>(DEFAULT_LABEL_CONFIG);

  const [exportProgress, setExportProgress] = useState<ExportProgress | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);

  // The label name defaults to the selected organization's name. Shares the
  // picker's query key, so it is normally served from cache.
  const orgsQuery = useQuery({
    queryKey: ['qr-generator', 'organizations'],
    queryFn: listOrganizations,
    enabled: !!orgId,
  });
  const orgName = orgsQuery.data?.organizations.find((org) => org.id === orgId)?.name;

  // A different organization starts from its own name again.
  // biome-ignore lint/correctness/useExhaustiveDependencies: reset keyed on orgId only
  useEffect(() => {
    setVenueName('');
    setVenueNameTouched(false);
  }, [orgId]);

  // Prefill only while the user hasn't typed; once they edit (even to clear
  // the field) their value wins.
  useEffect(() => {
    if (orgName && !venueNameTouched) setVenueName(orgName);
  }, [orgName, venueNameTouched]);

  const reload = useCallback(async () => {
    if (!orgId || !locationId) return;
    setLoading(true);
    try {
      const res = await listQrBatches(orgId, locationId);
      setBatches(res.batches);
      setLoadError(null);
    } catch (e) {
      setLoadError(translateApiError(e, t, { fallback: t('batches.loadError') }));
    } finally {
      setLoading(false);
    }
  }, [orgId, locationId, t]);

  useEffect(() => {
    void reload();
  }, [reload]);

  async function handleCreate(input: CreateQrBatchRequest) {
    if (!orgId || !locationId) return;
    setCreating(true);
    setCreateError(null);
    setCreateSuccess(null);
    try {
      const batch = await createQrBatch(orgId, locationId, input);
      setActiveBatch(batch);
      setCreateSuccess(t('create.success', { quantity: batch.quantity, prefix: batch.prefix }));
      void reload();
    } catch (e) {
      setCreateError(
        e instanceof ApiError
          ? translateApiError(e, t, { fallback: t('create.error') })
          : t('create.error'),
      );
    } finally {
      setCreating(false);
    }
  }

  async function handleReprint(batchId: string) {
    if (!orgId || !locationId) return;
    setReprintingId(batchId);
    setReprintError(null);
    try {
      const batch = await getQrBatch(orgId, locationId, batchId);
      setActiveBatch(batch);
    } catch (e) {
      setReprintError(translateApiError(e, t, { fallback: t('batches.reprintError') }));
    } finally {
      setReprintingId(null);
    }
  }

  async function handleExport() {
    if (!activeBatch) return;
    if (!venueName.trim()) {
      setExportError(t('venueName.required'));
      return;
    }
    setExportError(null);
    setExportProgress({ done: 0, total: activeBatch.codes.length });
    try {
      const { exportQrLabelSheets } = await import('../pdf/export-qr-labels');
      const input: ExportQrLabelsInput = {
        codes: activeBatch.codes,
        venueName: venueName.trim(),
        config: labelConfig,
        onProgress: (done, total) => setExportProgress({ done, total }),
      };
      await exportQrLabelSheets(input);
    } catch (e) {
      setExportError(translateApiError(e, t, { fallback: t('export.error') }));
    } finally {
      setExportProgress(null);
    }
  }

  const parts = activeBatch ? exportPartCount(activeBatch.codes.length) : 1;

  return (
    <div className="space-y-8">
      <PageHeader
        eyebrow={t('page.eyebrow')}
        title={t('page.title')}
        description={t('page.description')}
      />

      {loadError && (
        <p role="alert" className="text-[13px] text-jq-danger">
          {loadError}
        </p>
      )}

      <Card className="border-jq-sand/70 bg-jq-surface-0 shadow-sm">
        <CardContent className="p-6 sm:p-8">
          <QrBatchForm submitting={creating} onSubmit={(values) => void handleCreate(values)} />
          {createError && (
            <p role="alert" className="mt-3 text-[13px] text-jq-danger">
              {createError}
            </p>
          )}
          {createSuccess && (
            <p role="status" className="mt-3 text-[13px] text-jq-success">
              {createSuccess}
            </p>
          )}
        </CardContent>
      </Card>

      <Card className="border-jq-sand/70 bg-jq-surface-0 shadow-sm">
        <CardContent className="p-6 sm:p-8">
          <QrBatchList
            batches={batches}
            loading={loading}
            reprintingId={reprintingId}
            onReprint={(id) => void handleReprint(id)}
          />
          {reprintError && (
            <p role="alert" className="mt-3 text-[13px] text-jq-danger">
              {reprintError}
            </p>
          )}
        </CardContent>
      </Card>

      {activeBatch && (
        <Card className="border-jq-sand/70 bg-jq-surface-0 shadow-sm">
          <CardContent className="space-y-6 p-6 sm:p-8">
            <div>
              <Label htmlFor="venueName" className="mb-1.5 block text-[13px] font-medium">
                {t('venueName.label')}
              </Label>
              <Input
                id="venueName"
                value={venueName}
                onChange={(e) => {
                  setVenueNameTouched(true);
                  setVenueName(e.target.value);
                }}
                placeholder={t('venueName.placeholder')}
              />
            </div>

            <LabelConfigForm config={labelConfig} onChange={setLabelConfig} />

            <LabelGridPreview config={labelConfig} codes={activeBatch.codes} />

            {parts > 1 && (
              <p className="text-[12px] text-jq-graphite">
                {t('export.multiPartNote', { cap: MAX_LABELS_PER_PDF, parts })}
              </p>
            )}

            <div className="flex flex-col items-start gap-2">
              <Button
                type="button"
                size="sm"
                onClick={() => void handleExport()}
                disabled={exportProgress !== null}
              >
                {exportProgress
                  ? t('export.generating', {
                      done: exportProgress.done,
                      total: exportProgress.total,
                    })
                  : t('export.button')}
              </Button>
              {exportError && (
                <p role="alert" className="text-[13px] text-jq-danger">
                  {exportError}
                </p>
              )}
              {exportProgress && (
                <p role="status" className="text-[12px] text-jq-graphite">
                  {t('export.generating', {
                    done: exportProgress.done,
                    total: exportProgress.total,
                  })}
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
